import { SkyBadge, SkyText } from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import type { OverviewDestination } from "@shared/types/overview";
import { usePermissions } from "@/hooks/use-permissions";
import { PlayIcon } from "@/lib/icons";
import { NEW_CONNECTION_PATH, ROUTES } from "@/lib/routes";
import { askToSetDestination } from "@/lib/transformations/dbtSettings";
import { OverviewCard, OverviewLink } from "./OverviewCard";

/**
 * The first-run checklist (contract §3): it replaces the cards while the instance has no
 * connection, because every card would otherwise be a row of zeros.
 *
 * Viewer-safe: a step whose screen needs a write the role does not hold says who does it, rather
 * than offering a link that ends at a permission notice.
 */

function Step({
  index,
  title,
  done,
  children,
  testId,
}: {
  index: number;
  title: string;
  done: boolean | null;
  children: ReactNode;
  testId: string;
}) {
  return (
    <li className="flex flex-col gap-1 py-2" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <SkyText size={300} weight="semibold">
          {`${index}. ${title}`}
        </SkyText>
        {done === null ? null : (
          <SkyBadge appearance="tint" color={done ? "success" : "subtle"}>
            {done ? "Done" : "To do"}
          </SkyBadge>
        )}
      </div>
      <SkyText size={200} color="muted" block>
        {children}
      </SkyText>
    </li>
  );
}

export function FirstRunChecklist({
  destination,
}: {
  /** Null when the role cannot read the destination, or its read failed: the step has no badge. */
  destination: OverviewDestination | null;
}) {
  const { canAny } = usePermissions();
  const canSetDestination = canAny("instance.binding.write");
  const canReadDestination = canAny("instance.binding.read");
  const canCreateConnection = canAny("dataflows.write");
  const canSchedule = canAny("schedules.write");

  return (
    <OverviewCard id="first-run" title="Get started" icon={PlayIcon} testId="overview-first-run">
      <SkyText size={300} block>
        This instance has no connection yet. Three steps take it to its first import.
      </SkyText>
      <ol className="flex flex-col divide-y divide-border">
        <Step
          index={1}
          title="Set the destination"
          done={destination ? destination.bound : null}
          testId="overview-first-run-destination"
        >
          {canSetDestination ? (
            <>
              Where imported data lands.{" "}
              <OverviewLink to={ROUTES.settingsDestination}>Open Destination</OverviewLink>
            </>
          ) : (
            `Where imported data lands. ${
              destination?.bound
                ? "A platform admin set it."
                : askToSetDestination(canReadDestination)
            }`
          )}
        </Step>
        <Step
          index={2}
          title="Add a connection"
          done={false}
          testId="overview-first-run-connection"
        >
          {canCreateConnection ? (
            <>
              A source system to import from.{" "}
              <OverviewLink to={NEW_CONNECTION_PATH}>New connection</OverviewLink>
            </>
          ) : (
            "A source system to import from. An administrator adds it."
          )}
        </Step>
        <Step
          index={3}
          title="Schedule its imports"
          done={false}
          testId="overview-first-run-schedule"
        >
          {canSchedule ? (
            <>
              When each connection imports.{" "}
              <OverviewLink to={ROUTES.schedules}>Open Schedules</OverviewLink>
            </>
          ) : (
            "When each connection imports. An administrator schedules it."
          )}
        </Step>
      </ol>
    </OverviewCard>
  );
}
